import { CompanyRepository } from "core/repository";
import { BasePresenter } from "core/presenter";
import { CompanyConstraint } from "core/constraint";
import { toJS } from "mobx";
import { fn, validate } from "util";
import * as R from "ramda";
import _ from "lodash";

class CompanyFormCreatePresenter extends BasePresenter {
  initialState() {
    return {
      creating: true,
      company: null,
      loading: true,
      error: false,
      title: "Nova Empresa",
      formErrors: []
    };
  }

  async onStart() {
    this.loadCompany();
  }

  async loadCompany() {
    this.viewModel.loading = true;
    this.viewModel.error = false;
    const companyResult = await this.getCompany();
    if (companyResult.success) {
      const data = this.normalizeInData(companyResult.data);
      this.viewModel.company = data;
    } else {
      this.viewModel.error =
        "Não conseguimos carregar esta empresa! Tente novamente em alguns minutos.";

      await this.sendNotification({
        type: "error",
        title: "Oops...",
        content: this.viewModel.error
      });
    }

    this.viewModel.loading = false;
  }

  async getCompany() {
    return { success: true, data: {} };
  }

  normalizeInData(data) {
    [
      "name",
      "slug",
      "email",
      "phone",
      "receipt_additional_message",
      "notify_email"
    ]
      .filter(field => !data[field])
      .forEach(field => (data[field] = ""));

    ["fiscal_informations", "address", "integrations", "email_texts"]
      .filter(field => !data[field])
      .forEach(field => (data[field] = {}));

    ["napp", "my_finance", "mercado_pago"]
      .filter(field => !data.integrations[field])
      .forEach(field => (data.integrations[field] = {}));

    [
      "legal_name",
      "cnpj",
      "ie",
      "im",
      "accountant_email",
      "municipal_code",
      "crt_code",
      "iest_code",
      "cnae_code",
      "certificate",
      "certificate_password",
      "csc",
      "csc_id"
    ]
      .filter(field => !data.fiscal_informations[field])
      .forEach(field => (data.fiscal_informations[field] = ""));

    [
      "zipcode",
      "state",
      "city",
      "neighborhood",
      "street",
      "number",
      "complement"
    ]
      .filter(field => !data.address[field])
      .forEach(field => (data.address[field] = ""));

    data.max_installments = data.max_installments
      ? `${data.max_installments}`
      : null;

    ["name", "id", "user", "password", "url"]
      .filter(field => !data.integrations.napp[field])
      .forEach(field => (data.integrations.napp[field] = ""));

    ["sale_document"]
      .filter(field => !data.email_texts[field])
      .forEach(field => (data.email_texts[field] = " "));

    if (this.viewModel.creating) {
      data.blocked = false;
      data.integrations.napp.enabled = false;
      data.integrations.mercado_pago.enabled = false;
    }

    if (!this.viewModel.creating && !data.integrations.napp.enabled) {
      data.integrations.napp.enabled = false;
    }

    if (!this.viewModel.creating && !data.integrations.mercado_pago.enabled) {
      data.integrations.mercado_pago.enabled = false;
    }

    if (fn.isBlank(data.account_uuid)) {
      data.account_uuid = "";
    }

    this.setFeatureToShow("email_low_stock", data);
    this.setFeatureToShow("show_sellable_list", data);
    this.setFeatureToShow("print_sale_list", data);
    this.setFeatureToShow("print_detailed_sale", data);
    this.setFeatureToShow("show_sellable_image", data);
    this.setFeatureToShow("scan_barcode_by_camera", data);
    this.setFeatureToShow("block_sale_when_stock_is_zero", data);
    this.setFeatureToShow("open_sales", data);
    this.setFeatureToShow("customers", data);
    this.setCardSizeToShow(data);

    if (fn.isBlank(data.integrations.mercado_pago.token)) {
      data.integrations.mercado_pago.token = "";
    }

    return data;
  }

  existsOnFeatures(data, feature) {
    return (
      _.findIndex(data.features, i => {
        return i === feature;
      }) !== -1
    );
  }

  setCardSizeToShow(data) {
    const getSizeCardFromFeatures = () => {
      if (this.existsOnFeatures(data, "use_medium_cards")) {
        return "use_medium_cards";
      } else if (this.existsOnFeatures(data, "use_large_cards")) {
        return "use_large_cards";
      } else {
        return "use_normal_cards";
      }
    };
    data.size_card_mobile = getSizeCardFromFeatures();
  }

  setFeatureToShow(feature, data) {
    data[feature] = this.existsOnFeatures(data, feature);
  }

  setFeatureToSend(params, feature) {
    if (params[feature]) {
      if (
        _.findIndex(params.features, i => {
          return i === feature;
        }) === -1
      ) {
        params.features.push(feature);
      }
    } else {
      _.remove(params.features, i => {
        return i === feature;
      });
    }
    delete params[feature];
  }

  async onSendForm() {
    this.viewModel.loading = true;
    this.viewModel.formErrors = {};

    const params = await this.getRepositoryParams();
    const formErrors = this.formErrors(params);
    if (Object.keys(formErrors).length === 0) {
      params.max_installments = parseInt(params.max_installments, 10);

      if (!params.features) {
        params.features = [];
      }

      this.setFeatureToSend(params, "email_low_stock");
      this.setFeatureToSend(params, "show_sellable_list");
      this.setFeatureToSend(params, "print_sale_list");
      this.setFeatureToSend(params, "print_detailed_sale");
      this.setFeatureToSend(params, "show_sellable_image");
      this.setFeatureToSend(params, "scan_barcode_by_camera");
      this.setFeatureToSend(params, "block_sale_when_stock_is_zero");
      this.setFeatureToSend(params, "open_sales");
      this.setFeatureToSend(params, "customers");

      _.remove(
        params.features,
        item => item === "use_medium_cards" || item === "use_large_cards"
      );
      if (
        params.size_card_mobile &&
        params.size_card_mobile !== "use_normal_cards"
      ) {
        params.features.push(params.size_card_mobile);
      }

      const response = await this.callRepository(params);

      if (response.success) {
        this.sendNotification({
          type: "success",
          content: "A empresa foi salva!"
        });

        return this.navigateTo("app.registration.company");
      }

      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: response.fatal
          ? "Ocorreu um problema ao salvar a empresa."
          : response.error
      });
    } else {
      this.viewModel.formErrors = formErrors;

      const fieldName = {
        name: "Nome",
        slug: "Apelido",
        email: "E-mail",
        phone: "Telefone",
        notify_email: "E-mail de notificação de estoque mínimo",
        max_installments: "Quantidade máxima de parcelas",
        "fiscal_informations.legal_name": "Razão Social",
        "fiscal_informations.cnpj": "CNPJ",
        "fiscal_informations.ie": "Inscrição Estadual",
        "fiscal_informations.im": "Inscrição Municipal",
        "fiscal_informations.municipal_code": "Código do Município",
        "fiscal_informations.accountant_email": "Email da contabilidade",
        "fiscal_informations.crt_code": "Código CRT",
        "fiscal_informations.cnae_code": "Código CNAE",
        "fiscal_informations.iest_code": "Código IEST",
        "fiscal_informations.csc_id": "ID do CSC",
        "fiscal_informations.certificate_password": "Senha do Certificado",
        "address.zipcode": "CEP",
        "address.state": "Estado"
      };

      const invalidFieldsMap = Object.keys(formErrors)
        .map(field => fieldName[field])
        .join("\n- ");
      const invalidFields = `- ${invalidFieldsMap}`;

      this.sendNotification({
        type: "error",
        title: "Oops...",
        content: `Ocorreu um problema ao salvar a empresa. Verifique os dados informados para o(s) seguinte(s) campo(s):\n\n${invalidFields}`
      });
    }

    this.viewModel.loading = false;
  }

  formErrors(params) {
    const errors = {};

    const constraint = CompanyConstraint.general;
    constraint.notify_email.presence.allowEmpty = !params.email_low_stock;
    const validation = validate(params, constraint, { fullMessages: false });

    if (!!validation) {
      Object.entries(validation)
        .map(([field, errors]) => ({ field, content: errors.join(", ") }))
        .forEach(({ field, content }) => (errors[field] = content));
    }

    return errors;
  }

  onGoBack() {
    this.navigateTo("app.registration.company");
  }

  async callRepository(params) {
    return await CompanyRepository.create(params);
  }

  updateVmMyFinanceIntegration(params) {
    const updatedMyFinanceIntegration = params;
    const normalizedData = this.normalizeInData(updatedMyFinanceIntegration);

    this.viewModel.company.integrations.my_finance = {
      account_id: normalizedData.account_id,
      enabled: normalizedData.enabled,
      entity_id: normalizedData.entity_id,
      token: normalizedData.token
    };
  }

  unmaskField(value) {
    return !!value ? value.replace(/\D/g, "") : "";
  }

  async getRepositoryParams() {
    const data = toJS(this.viewModel.company);
    const updatedCompany = await this.getCompany();

    if (updatedCompany.success) {
      if (R.pathOr(false, ["data", "integrations", "tray"])(updatedCompany)) {
        data.integrations.tray = updatedCompany.data.integrations.tray;
      }
    }

    if (fn.isBlank(data.phone)) {
      data.phone = null;
    } else {
      data.phone = data.phone.replace(/\D/g, "");
    }

    if (fn.isBlank(data.account_uuid)) {
      const currentUser = await this.mRuntime.get("currentUser");
      data.account_uuid = currentUser.account_uuid;
    }

    data.fiscal_informations.cnpj = this.unmaskField(
      data.fiscal_informations.cnpj
    );
    data.address.zipcode = this.unmaskField(data.address.zipcode);
    data.fiscal_informations.cnae_code = this.unmaskField(
      data.fiscal_informations.cnae_code
    );
    data.address.address_type = "rua";

    if (data.receipt_additional_message.substring(-1, 1) !== "\n") {
      data.receipt_additional_message = `${data.receipt_additional_message}\n`;
    }

    delete data.management_until;

    if (fn.isBlank(data.slug)) {
      data.slug = data.name;
    }

    if (fn.isBlank(data.email_texts.sale_document)) {
      delete data.email_texts.sale_document;
    }

    return data;
  }
}

export default CompanyFormCreatePresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/company/form/company-form-create.presenter.js